import React, { useRef, useEffect, useState } from 'react';

// Lichtgewicht rich-text: vet/cursief/opsomming + opmaak-behoud bij plakken (uit Word/web).
// Geen dependencies; slaat een veilige HTML-subset op als string. Helpers hieronder maken de
// waarde ook bruikbaar als platte tekst (AI-context, leegcontrole, PDF/Markdown-export).

const ALLOWED = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'BR', 'P', 'A']);

function sanitizeNode(node: Node): Node | null {
  if (node.nodeType === Node.TEXT_NODE) return node.cloneNode();
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const el = node as HTMLElement;
  const tag = el.tagName;
  if (!ALLOWED.has(tag)) {
    // Niet-toegestane tag: uitpakken, kinderen (opgeschoond) behouden.
    const frag = document.createDocumentFragment();
    el.childNodes.forEach((c) => { const s = sanitizeNode(c); if (s) frag.appendChild(s); });
    return frag;
  }
  const clean = document.createElement(tag.toLowerCase());
  if (tag === 'A') {
    const href = el.getAttribute('href') || '';
    if (/^https?:\/\//i.test(href)) { clean.setAttribute('href', href); clean.setAttribute('target', '_blank'); clean.setAttribute('rel', 'noopener noreferrer'); }
  }
  el.childNodes.forEach((c) => { const s = sanitizeNode(c); if (s) clean.appendChild(s); });
  return clean;
}

export function sanitizeHtml(html: string): string {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const out = document.createElement('div');
  tmp.childNodes.forEach((c) => { const s = sanitizeNode(c); if (s) out.appendChild(s); });
  return out.innerHTML;
}

export function isHtml(s?: string): boolean {
  return typeof s === 'string' && /<(b|strong|i|em|u|ul|ol|li|br|p|a)\b[^>]*>/i.test(s);
}

// HTML → platte tekst (met streepjes voor lijstitems, regeleindes voor blokken).
export function htmlToPlain(html?: string): string {
  if (!html) return '';
  if (!isHtml(html) && !/[<&]/.test(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  const walk = (node: Node): string => {
    let out = '';
    node.childNodes.forEach((c) => {
      if (c.nodeType === Node.TEXT_NODE) out += c.textContent || '';
      else if (c.nodeType === Node.ELEMENT_NODE) {
        const el = c as HTMLElement;
        const tag = el.tagName;
        if (tag === 'BR') out += '\n';
        else if (tag === 'LI') out += '- ' + walk(c).trim() + '\n';
        else if (tag === 'P' || tag === 'DIV') out += walk(c).trim() + '\n';
        else out += walk(c);
      }
    });
    return out;
  };
  return walk(div).replace(/\n{3,}/g, '\n\n').trim();
}

export function htmlHasText(html?: string): boolean {
  return htmlToPlain(html).trim().length > 0;
}

// HTML → Markdown (voor de Markdown-export): **vet**, *cursief*, - opsomming, [link](url).
export function htmlToMarkdown(html?: string): string {
  if (!html) return '';
  if (!isHtml(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = sanitizeHtml(html);
  const inline = (node: Node): string => {
    let out = '';
    node.childNodes.forEach((c) => {
      if (c.nodeType === Node.TEXT_NODE) out += c.textContent || '';
      else if (c.nodeType === Node.ELEMENT_NODE) {
        const el = c as HTMLElement; const t = el.tagName;
        if (t === 'B' || t === 'STRONG') out += `**${inline(c).trim()}**`;
        else if (t === 'I' || t === 'EM') out += `*${inline(c).trim()}*`;
        else if (t === 'A') out += `[${inline(c)}](${el.getAttribute('href') || ''})`;
        else if (t === 'BR') out += '\n';
        else out += inline(c);
      }
    });
    return out;
  };
  const blocks: string[] = [];
  div.childNodes.forEach((c) => {
    if (c.nodeType === Node.ELEMENT_NODE && ((c as HTMLElement).tagName === 'UL' || (c as HTMLElement).tagName === 'OL')) {
      (c as HTMLElement).childNodes.forEach((li) => { if (li.nodeType === Node.ELEMENT_NODE && (li as HTMLElement).tagName === 'LI') { const t = inline(li).trim(); if (t) blocks.push('- ' + t); } });
    } else if (c.nodeType === Node.ELEMENT_NODE && (c as HTMLElement).tagName === 'P') {
      const t = inline(c).trim(); if (t) blocks.push(t);
    } else if (c.nodeType === Node.TEXT_NODE) {
      const t = (c.textContent || '').trim(); if (t) blocks.push(t);
    } else { const t = inline(c).trim(); if (t) blocks.push(t); }
  });
  return blocks.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export interface RtRun { text: string; bold?: boolean; italic?: boolean; nl?: boolean; }
export interface RtBlock { bullet?: boolean; runs: RtRun[]; }

// HTML → blokken met opgemaakte runs (voor de PDF-export met jsPDF).
export function htmlToBlocks(html?: string): RtBlock[] {
  const blocks: RtBlock[] = [];
  if (!html) return blocks;
  const div = document.createElement('div');
  div.innerHTML = sanitizeHtml(html);
  const collect = (node: Node, bold: boolean, italic: boolean, runs: RtRun[]) => {
    node.childNodes.forEach((c) => {
      if (c.nodeType === Node.TEXT_NODE) { const t = c.textContent || ''; if (t) runs.push({ text: t, bold, italic }); }
      else if (c.nodeType === Node.ELEMENT_NODE) {
        const el = c as HTMLElement; const tg = el.tagName;
        if (tg === 'BR') { runs.push({ text: '', nl: true }); return; }
        collect(c, bold || tg === 'B' || tg === 'STRONG', italic || tg === 'I' || tg === 'EM', runs);
      }
    });
  };
  const pushBlock = (node: Node, bullet: boolean) => { const runs: RtRun[] = []; collect(node, false, false, runs); if (runs.some((r) => r.nl || (r.text || '').trim())) blocks.push({ bullet, runs }); };
  const walk = (node: Node) => {
    node.childNodes.forEach((c) => {
      if (c.nodeType === Node.ELEMENT_NODE) {
        const el = c as HTMLElement; const tg = el.tagName;
        if (tg === 'UL' || tg === 'OL') el.childNodes.forEach((li) => { if (li.nodeType === Node.ELEMENT_NODE && (li as HTMLElement).tagName === 'LI') pushBlock(li, true); });
        else if (tg === 'P') pushBlock(c, false);
        else if (['B', 'STRONG', 'I', 'EM', 'A', 'U'].includes(tg)) pushBlock(c, false);
        else walk(c);
      } else if (c.nodeType === Node.TEXT_NODE) { const t = c.textContent || ''; if (t.trim()) blocks.push({ runs: [{ text: t }] }); }
    });
  };
  walk(div);
  if (blocks.length === 0) pushBlock(div, false);
  return blocks;
}

const btnStyle: React.CSSProperties = { minWidth: 30, height: 26, border: '1px solid #e5e7eb', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#334155', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

interface RichTextProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
  /** Lange teksten ingeklapt tonen met "Toon meer" (rustiger scherm, o.a. voor demo's). */
  inklapbaar?: boolean;
}

// Hoogte waarboven een inklapbaar veld wordt ingekort.
const INKLAP_HOOGTE = 150;

export const RichText: React.FC<RichTextProps> = ({ value, onChange, disabled, placeholder, minHeight = 88, inklapbaar = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const leesRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [teLang, setTeLang] = useState(false);

  // Externe waarde inladen wanneer het veld niet bewerkt wordt (voorkomt cursor-sprongen).
  // Ook opnieuw doen als het veld van lezen naar bewerken gaat (heropenen na validatie): dan
  // wordt een nieuw bewerkbaar element gemount dat anders leeg blijft, terwijl de waarde niet wijzigt.
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value, disabled]);

  // Na het vullen meten of de tekst langer is dan de inklaphoogte.
  useEffect(() => {
    if (!inklapbaar) return;
    const el = disabled ? leesRef.current : ref.current;
    if (el) setTeLang(el.scrollHeight > INKLAP_HOOGTE + 30);
  }, [value, disabled, inklapbaar]);

  const ingeklapt = inklapbaar && teLang && !open;
  const inklapStijl: React.CSSProperties = ingeklapt ? { maxHeight: INKLAP_HOOGTE, overflow: 'hidden' } : {};
  const vervaging = (achtergrond: string) => (ingeklapt ? (
    <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 48, background: `linear-gradient(to bottom, rgba(255,255,255,0), ${achtergrond})`, pointerEvents: 'none' }} />
  ) : null);
  const toggle = inklapbaar && teLang ? (
    <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
      style={{ marginTop: 6, padding: 0, border: 'none', background: 'none', color: '#1e3a2f', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
      {open ? 'Toon minder' : 'Toon meer'}
    </button>
  ) : null;

  const emit = () => { if (ref.current) onChange(sanitizeHtml(ref.current.innerHTML)); };

  const cmd = (c: string) => { document.execCommand(c); ref.current?.focus(); emit(); };

  const onPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    let insert: string;
    if (html) insert = sanitizeHtml(html);
    else insert = (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
    document.execCommand('insertHTML', false, insert);
    emit();
  };

  if (disabled) {
    return (
      <div>
        {/* Kader om de inhoud heen, zodat de rand zichtbaar blijft als de tekst is ingeklapt */}
        <div style={{ position: 'relative', borderRadius: 8, border: '1px solid #d8dce4', background: '#f6f7f9', overflow: 'hidden' }}>
          <div
            ref={leesRef}
            className="rt-content"
            style={{ minHeight, padding: '10px 12px', fontSize: 14, lineHeight: 1.55, color: '#20242e', whiteSpace: 'pre-wrap', ...inklapStijl }}
            dangerouslySetInnerHTML={{ __html: value || '' }}
          />
          {vervaging('#f6f7f9')}
        </div>
        {toggle}
      </div>
    );
  }

  const tb = (e: React.MouseEvent) => e.preventDefault();
  return (
    <div>
    <div style={{ border: '1px solid #d8dce4', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display: 'flex', gap: 6, padding: '6px 8px', borderBottom: '1px solid #eef0f4', background: '#fafbfc' }}>
        <button type="button" onMouseDown={tb} onClick={() => cmd('bold')} title="Vet" style={{ ...btnStyle, fontWeight: 800 }}>B</button>
        <button type="button" onMouseDown={tb} onClick={() => cmd('italic')} title="Cursief" style={{ ...btnStyle, fontStyle: 'italic' }}>I</button>
        <button type="button" onMouseDown={tb} onClick={() => cmd('insertUnorderedList')} title="Opsomming" style={{ ...btnStyle, minWidth: 34 }}>• —</button>
      </div>
      <div style={{ position: 'relative' }}>
      <div
        ref={ref}
        className="rt-content rt-editable"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        onFocus={() => { if (ingeklapt) setOpen(true); }}
        data-placeholder={placeholder || ''}
        style={{ minHeight, padding: '10px 12px', fontSize: 14, lineHeight: 1.55, color: '#20242e', outline: 'none', ...inklapStijl }}
      />
      {vervaging('#ffffff')}
      </div>
    </div>
    {toggle}
    </div>
  );
};
